import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';

export interface ErrorMessage {
  id: number;
  message: string;
  traceId: string;
}

@Injectable({ providedIn: 'root' })
export class ErrorMessageService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly errorsSubject = new BehaviorSubject<ErrorMessage[]>([]);

  private readonly maxErrors = 5;
  private nextId = 0;

  readonly errors$ = this.errorsSubject.asObservable();

  constructor() {
    const router = inject(Router);
    router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.clear());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  add(message: string, traceId: string): void {
    const error: ErrorMessage = {
      id: this.nextId++,
      message,
      traceId,
    };

    const errors = [error, ...this.errorsSubject.value].slice(
      0,
      this.maxErrors,
    );
    this.errorsSubject.next(errors);
  }

  remove(id: number): void {
    const errors = this.errorsSubject.value;
    this.errorsSubject.next(errors.filter((e) => e.id !== id));
  }

  private clear(): void {
    this.errorsSubject.next([]);
  }
}
